import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ReactNode } from 'react'
import {
  ApiError,
  UnauthorizedError,
  documentsApi,
  type CatalogDocumentDTO,
} from '@/shared/api'
import { useAuth } from '@/features/auth'
import { documentKey, scopeFromDocument } from './scopeUtils'
import type {
  RagScope,
  ScopeContextValue,
  ScopeState,
} from './types'

/**
 * Holds the user's active retrieval scope plus the catalog snapshot needed
 * by the selector UI. Scope selection lives **in memory only** during this
 * step — we never write tenant ids, document ids or scope coordinates to
 * LocalStorage/SessionStorage. Tokens are out of the question entirely
 * (cookie-only auth model).
 *
 * Step 8.3 — Multi-document Case Scope:
 *   The provider now tracks an ordered list of case documents in
 *   ``selectedCaseDocuments``. The legacy ``selected: RagScope | null`` is
 *   derived from the first element (or ``null`` when nothing is selected)
 *   so older single-coordinate consumers (DocumentsPage badges, scope
 *   equality checks) keep working without churn. ``toggleCaseDocument``
 *   and ``clearCaseDocuments`` are the new multi-doc entry points.
 */

const INITIAL_STATE: ScopeState = {
  documents: [],
  status: 'idle',
  error: null,
  selected: null,
  selectedCaseDocuments: [],
}

const ScopeContext = createContext<ScopeContextValue | null>(null)

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.detail || fallback
  if (err instanceof Error) return err.message
  return fallback
}

interface ProviderProps {
  children: ReactNode
}

/**
 * Recompute the legacy ``selected: RagScope | null`` from the multi-doc
 * list so older single-coordinate consumers keep working. We use the first
 * entry as the representative scope when exactly one document is selected;
 * for multi-doc selections we still expose the first entry's scope so the
 * badge can render a sensible default — the source of truth for retrieval
 * is always ``case_document_ids``, which the send pipeline reads from
 * ``selectedCaseDocuments`` directly.
 */
function deriveSelected(docs: CatalogDocumentDTO[]): RagScope | null {
  if (docs.length === 0) return null
  return scopeFromDocument(docs[0])
}

export function ScopeProvider({ children }: ProviderProps) {
  const { organization, status: authStatus, logout } = useAuth()
  const [state, setState] = useState<ScopeState>(INITIAL_STATE)
  const requestedTenantRef = useRef<string | null>(null)

  const tenantId = organization?.tenant_id ?? null

  const reload = useCallback(async (): Promise<CatalogDocumentDTO[]> => {
    if (!tenantId) return []
    setState((prev) => ({ ...prev, status: 'loading', error: null }))
    try {
      // Step 8.0 — o seletor de escopo da chat opera somente sobre
      // case_documents. Documentos da Biblioteca de Referência ainda não
      // entram na recuperação automática (multi-context RAG é a próxima
      // etapa). Eles permanecem isolados na rota /reference-library.
      const response = await documentsApi.listDocuments({
        tenantId,
        documentKind: 'case_document',
      })
      setState((prev) => {
        // Defensive: a well-behaved backend always returns ``items``; the
        // ``?? []`` keeps a malformed/empty envelope from throwing inside this
        // state updater (which React runs during render — an uncaught throw
        // here would blank the app). The api client already rejects non-JSON
        // 2xx responses, so this is belt-and-suspenders.
        const documents = response.items ?? []
        // Drop selected documents that no longer exist in the fresh catalog
        // (e.g. deleted by another tab or the DocumentsPage trash flow). The
        // match is on the full coordinate tuple — otherwise a deleted entry
        // could silently inherit selection from a different tuple that just
        // happens to share the same document_id.
        const validKeys = new Set(documents.map((d) => documentKey(d)))
        const filtered = prev.selectedCaseDocuments.filter((d) =>
          validKeys.has(documentKey(d)),
        )
        return {
          documents,
          status: 'ready',
          error: null,
          selectedCaseDocuments: filtered,
          selected: deriveSelected(filtered),
        }
      })
      return response.items
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        await logout()
        return []
      }
      setState((prev) => ({
        ...prev,
        status: 'error',
        error: errorMessage(err, 'Falha ao carregar o catálogo.'),
      }))
      // Match the historical "swallow + record error in state" contract so
      // existing callers (DocumentsPage upload/delete flows) keep working —
      // they relied on the promise resolving rather than rejecting. Callers
      // that need to verify a specific document landed (guided ingestion)
      // should treat an empty/missing return as "could not confirm".
      return []
    }
  }, [tenantId, logout])

  // (Re)load catalog whenever the session/tenant changes. Avoid re-issuing
  // the same fetch in React StrictMode by tagging the active tenant.
  useEffect(() => {
    if (authStatus !== 'authenticated' || !tenantId) {
      requestedTenantRef.current = null
      setState(INITIAL_STATE)
      return
    }
    if (requestedTenantRef.current === tenantId) return
    requestedTenantRef.current = tenantId
    void reload()
  }, [authStatus, tenantId, reload])

  const selectDocument = useCallback(
    (doc: CatalogDocumentDTO) => {
      // Single-doc replacement — mirrors the pre-8.3 "Usar no chat" /
      // single-row click semantics. The full multi-doc list collapses to
      // exactly this document. Callers that need the additive behavior use
      // ``toggleCaseDocument`` instead.
      setState((prev) => {
        const key = documentKey(doc)
        if (
          prev.selectedCaseDocuments.length === 1 &&
          documentKey(prev.selectedCaseDocuments[0]) === key
        ) {
          return prev
        }
        const next = [doc]
        return {
          ...prev,
          selectedCaseDocuments: next,
          selected: deriveSelected(next),
        }
      })
    },
    [],
  )

  const toggleCaseDocument = useCallback((doc: CatalogDocumentDTO) => {
    setState((prev) => {
      const key = documentKey(doc)
      const has = prev.selectedCaseDocuments.some(
        (d) => documentKey(d) === key,
      )
      const next = has
        ? prev.selectedCaseDocuments.filter((d) => documentKey(d) !== key)
        : [...prev.selectedCaseDocuments, doc]
      return {
        ...prev,
        selectedCaseDocuments: next,
        selected: deriveSelected(next),
      }
    })
  }, [])

  const clearScope = useCallback(() => {
    setState((prev) =>
      prev.selectedCaseDocuments.length === 0 && prev.selected === null
        ? prev
        : {
            ...prev,
            selectedCaseDocuments: [],
            selected: null,
          },
    )
  }, [])

  // Alias — named explicitly so multi-doc call sites read naturally.
  const clearCaseDocuments = clearScope

  const value = useMemo<ScopeContextValue>(
    () => ({
      ...state,
      reload,
      selectDocument,
      toggleCaseDocument,
      clearCaseDocuments,
      clearScope,
    }),
    [state, reload, selectDocument, toggleCaseDocument, clearCaseDocuments, clearScope],
  )

  return (
    <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>
  )
}

export function useScope(): ScopeContextValue {
  const ctx = useContext(ScopeContext)
  if (!ctx) {
    throw new Error('useScope must be used inside <ScopeProvider>')
  }
  return ctx
}
